import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useChartColors } from './theme';
import { useTooltip } from './ChartTooltip';
import TableView from './TableView';

export interface ColumnDatum { label: string; value: number; }
interface ColumnsProps {
    data: ColumnDatum[];
    height?: number;
    colorIndex?: number;
    yFmt?: (n: number) => string;
    marker?: { index: number; label: string };
    max?: number;
}

// Ported from docs/dashboard-redesign/mockups/assets/viz.js `columns`.
// niceMax picks a round axis top; bars are thin with a 4px rounded top anchored to the baseline.
function niceMax(max: number): number {
    if (max <= 0) return 1;
    const mults = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
    const pow = Math.pow(10, Math.floor(Math.log10(max)));
    for (const m of mults) if (pow * m >= max) return pow * m;
    return pow * 10;
}

export default function Columns({ data, height = 200, colorIndex = 0, yFmt, marker, max }: ColumnsProps) {
    const c = useChartColors();
    const { show, hide, node } = useTooltip();
    const [showTable, setShowTable] = useState(false);

    const W = 520, H = height, padL = yFmt ? 44 : 32, padB = 26, padT = 10;
    const top = max !== undefined ? niceMax(Math.max(1, max)) : niceMax(Math.max(1, ...data.map(d => d.value)));
    const plotH = H - padB - padT, plotW = W - padL - 8;
    const bw = Math.min(38, (plotW / data.length) * 0.62);
    const step = plotW / data.length;
    const fmt = yFmt ?? ((n: number) => String(n));
    const markerX = marker && marker.index >= 0 && marker.index < data.length
        ? padL + step * marker.index + step / 2
        : null;

    return (
        <Box>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="bar chart">
                {[0, 0.25, 0.5, 0.75, 1].map((g, i) => {
                    const y = padT + plotH * (1 - g);
                    return (
                        <g key={i}>
                            <line x1={padL} y1={y} x2={W - 8} y2={y} stroke={c.grid} strokeWidth={1} />
                            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={10} fill={c.muted}>{fmt(top * g)}</text>
                        </g>
                    );
                })}
                {data.map((d, i) => {
                    const h = plotH * (d.value / top);
                    const x = padL + step * i + (step - bw) / 2;
                    const y = padT + plotH - h;
                    return (
                        <g key={i}
                           onMouseMove={e => show(e.clientX, e.clientY, `${d.label}: ${fmt(d.value)}`)}
                           onMouseLeave={hide}>
                            <rect x={x} y={y} width={bw} height={Math.max(0, h)} rx={3} fill={c.series[colorIndex]} />
                            <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize={10} fill={c.muted}>{d.label}</text>
                        </g>
                    );
                })}
                {markerX !== null && marker && (
                    <g pointerEvents="none">
                        <line x1={markerX} y1={padT} x2={markerX} y2={padT + plotH} stroke={c.series[1]} strokeWidth={1.5} strokeDasharray="4 3" />
                        <text x={markerX} y={padT - 2} textAnchor="middle" fontSize={10} fill={c.series[1]}>{marker.label}</text>
                    </g>
                )}
            </svg>
            <Button size="small" onClick={() => setShowTable(s => !s)}>Table view</Button>
            {showTable && <TableView columns={['Label', 'Value']} rows={data.map(d => [d.label, fmt(d.value)])} />}
            {node}
        </Box>
    );
}
